/**
 * What a permission is (ADR-0019).
 *
 * A feature is a capability of the product. Using a feature sometimes means
 * taking an action, and a permission is whether this staff member may take it.
 *
 * A permission is a kind of setting that sits on top of a staff feature:
 * - it lists the actions it allows, so "who can do X" is one lookup;
 * - it only counts while its feature is on;
 * - it is off until granted, to a role or to one staff member;
 * - it is added only where it says something the feature cannot: the feature
 *   hides actions we would grant to different people, or the action needs a
 *   limit or an approval. An action no permission covers is gated by its
 *   feature alone.
 *
 * Back office only. Customers have features, ownership rules and safety
 * restrictions, never permissions.
 *
 * It lives beside `../settings` rather than in it because it names actions,
 * and actions already name settings.
 */

import type { ActionId, StaffFeatureId } from "../actions";
import type { Sensitivity, SettingBase, Status } from "../settings/types";

export type PermissionId = `${string}_STAFF_PERMISSION`;
export type PermissionLimitId = `${string}_STAFF_PERMISSION_LIMIT`;

/** A staff action that exists in ACTIONS. A typo is a compile error. */
export type StaffActionIdOf = Extract<ActionId, `${string}_STAFF_ACTION`>;

/** An amount with its currency (ISO 4217). A limit is never a bare number. */
export type MoneyLimit = { currency: string; amount: number };

/**
 * The most the holder may do alone, as its own money setting held at the same
 * scopes as the grant: one value per currency. No limit at all means no
 * ceiling. With limits, an amount in a currency the holder has no limit in is
 * above every limit: there is no rate to compare it with, so they ask.
 */
export interface PermissionLimit<I extends PermissionLimitId = PermissionLimitId> extends SettingBase<
  I,
  "Money",
  false
> {
  kind: "permission_limit";
  scopes: readonly ["staffRole", "staffMember"];
  /** The permission it limits. */
  permission: PermissionId;
}

export interface Permission<I extends PermissionId = PermissionId> extends SettingBase<I, "Boolean", false> {
  kind: "permission";
  /** English, the source text. The other languages translate it, keyed by `settingId`. */
  name: string;
  /** The staff feature it sits on. The permission is dead while this is off. */
  feature: StaffFeatureId;
  /** The staff write actions it allows. An action is covered by at most one permission. */
  actions: readonly StaffActionIdOf[];
  scopes: readonly ["staffRole", "staffMember"];
  /** Always off until granted. */
  defaultValue: false;
  /** Who may grant it (ADR-0013 §2.2). Nobody grants anything to themselves. */
  sensitivity: Sensitivity;
  /**
   * What happens to someone who holds the feature but not the permission, or
   * who is above their limit. `true`: their attempt becomes an approval
   * request. `false`: it is refused.
   */
  approvable: boolean;
  /** Its amount limit, or null when the permission has none. */
  limit: PermissionLimit<`${I}_LIMIT` & PermissionLimitId> | null;
}

export function definePermission<const I extends PermissionId>(permission: {
  settingId: I;
  name: string;
  feature: StaffFeatureId;
  actions: readonly StaffActionIdOf[];
  category: Permission["category"];
  sensitivity: Sensitivity;
  approvable: boolean;
  /** `"amount"`: holders can be given a ceiling, per currency. */
  limit?: "amount";
  status?: Status;
}): Permission<I> {
  const status = permission.status ?? "active";
  return {
    settingId: permission.settingId,
    kind: "permission",
    name: permission.name,
    feature: permission.feature,
    actions: permission.actions,
    category: permission.category,
    scopes: ["staffRole", "staffMember"],
    valueType: "Boolean",
    isList: false,
    defaultValue: false,
    sensitivity: permission.sensitivity,
    approvable: permission.approvable,
    limit:
      permission.limit === "amount"
        ? {
            settingId: `${permission.settingId}_LIMIT` as `${I}_LIMIT` & PermissionLimitId,
            name: `${permission.name}: limit`,
            kind: "permission_limit",
            category: permission.category,
            scopes: ["staffRole", "staffMember"],
            valueType: "Money",
            isList: false,
            defaultValue: 0,
            status,
            permission: permission.settingId,
          }
        : null,
    status,
  };
}
